<script setup lang="ts">
/**
 * NavigationButtonPanel – sidebar tab buttons driven by Vue Router; each tab is a route.
 *
 * Fixed-position panel (vertically centered on the viewport) matching the original
 * instrument-bar look. The shared `useSidebarPanel` collapse flag slides it off-screen
 * and flips `aria-hidden` without changing its layout when visible.
 */
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSidebarPanel } from '@/composables/useSidebarPanel'
import { useLogout } from '@/composables/useLogout'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import LocaleSwitcher from '@/components/sidebar/LocaleSwitcher.vue'

export interface TabItem {
  // One sidebar tab: route target and button label.

  path: string
  // Vue Router path for this tab.

  label: string
  // Visible text on the tab button.

  onAdd?: () => void
  // Optional handler to open a create form for this tab's entity.
}

defineProps<{
  /** Tabs: each has a route path and button label. Active tab is the current route. */
  tabs: TabItem[]
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { collapsed } = useSidebarPanel()
const { showLogoutConfirm, loggingOut, requestLogout, confirmLogout, cancelLogout } = useLogout()

function isActive(path: string): boolean {
  return route.path === path
}

function goTo(path: string) {
  if (route.path !== path) {
    router.push(path)
  }
}
</script>

<template>
  <nav
    id="instrument-sidebar"
    class="fixed left-0 top-1/2 z-10 flex -translate-y-1/2 flex-col border-r border-tufte-border bg-tufte-bg font-tufte text-tufte-text transition-transform duration-200 ease-out motion-reduce:transition-none"
    :class="collapsed ? '-translate-x-full' : ''"
    :style="{ width: 'var(--width-instrument-bar)' }"
    :aria-hidden="collapsed"
    :aria-label="t('nav.tabNavigation')"
  >
    <div
      v-for="(tab, index) in tabs"
      :key="tab.path"
      class="group relative border-b border-solid border-tufte-border last:border-b-0"
    >
      <button
        type="button"
        class="w-full border-0 bg-transparent py-3 pl-4 pr-8 text-left text-tufte-lg transition-colors hover:bg-tufte-border/30 focus:outline-none focus:ring-2 focus:ring-tufte-text/30"
        :class="{
          'bg-tufte-border/20 font-bold': isActive(tab.path),
          'text-tufte-muted': !isActive(tab.path),
        }"
        :aria-selected="isActive(tab.path)"
        :aria-controls="`panel-${index}`"
        :id="`tab-${index}`"
        @click="goTo(tab.path)"
      >
        {{ tab.label }}
      </button>
      <button
        v-if="tab.onAdd"
        type="button"
        class="absolute right-1 top-1 flex h-6 w-6 cursor-pointer items-center justify-center border-0 bg-transparent p-0 font-tufte-sans text-lg leading-none text-tufte-muted opacity-30 transition-[opacity,color] duration-[120ms] group-hover:opacity-100 hover:text-tufte-text focus:outline-none"
        :title="t('nav.addNew')"
        @click.stop="tab.onAdd()"
      >
        +
      </button>
    </div>

    <button
      type="button"
      class="w-full cursor-pointer border-0 border-t border-solid border-tufte-border bg-transparent py-3 pl-4 pr-8 text-left text-tufte-lg text-tufte-muted transition-colors hover:bg-tufte-border/30 hover:text-tufte-text focus:outline-none focus:ring-2 focus:ring-tufte-text/30 disabled:cursor-wait disabled:opacity-50"
      :disabled="loggingOut"
      @click="requestLogout"
    >
      {{ t('nav.logOut') }}
    </button>

    <div class="border-t border-solid border-tufte-border px-4 py-3">
      <LocaleSwitcher />
    </div>
  </nav>

  <DeleteConfirmSheet
    :open="showLogoutConfirm"
    centered
    :title="t('logout.confirmTitle')"
    :description="t('logout.confirmDescription')"
    :confirm-label="t('logout.confirmLabel')"
    :pending="loggingOut"
    @confirm="confirmLogout"
    @cancel="cancelLogout"
  />
</template>
